import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import api from '../utils/api';
import { formatPrice, statusColor, statusLabel } from '../utils/helpers';
import toast from 'react-hot-toast';
import './OrderDetailPage.css';

const METHODS = [
  { key: 'bkash',  label: 'bKash',  color: '#E2136E', bg: '#fce4ef', icon: '💳' },
  { key: 'nagad',  label: 'Nagad',  color: '#F7941D', bg: '#fff3e0', icon: '💰' },
  { key: 'rocket', label: 'Rocket', color: '#8B008B', bg: '#f3e5f5', icon: '🚀' },
  { key: 'upay',   label: 'Upay',   color: '#00A859', bg: '#e8f5e9', icon: '📲' },
];

const OrderDetailPage = () => {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [marking, setMarking] = useState(false);
  const [paymentSettings, setPaymentSettings] = useState(null);
  const [senderNumber, setSenderNumber] = useState('');
  const [savingSender, setSavingSender] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get(`/orders/${id}`),
      api.get('/payment-settings').catch(() => ({ data: null })),
    ]).then(([orderRes, payRes]) => {
      setOrder(orderRes.data);
      setPaymentSettings(payRes.data);
      if (orderRes.data.senderNumber) setSenderNumber(orderRes.data.senderNumber);
    }).finally(() => setLoading(false));
  }, [id]);

  const markAsPaid = async () => {
    setMarking(true);
    try {
      await api.put(`/orders/${id}/markpaid`);
      setOrder(prev => ({ ...prev, customerMarkedPaid: true }));
      toast.success('Payment marked! Admin will verify shortly.');
    } catch (err) {
      toast.error('Failed to update');
    } finally {
      setMarking(false);
    }
  };

  const saveSenderNumber = async (e) => {
    e.preventDefault();
    if (!senderNumber.trim()) { toast.error('Enter the number you sent from'); return; }
    setSavingSender(true);
    try {
      await api.put(`/orders/${id}/payment-sender`, { senderNumber: senderNumber.trim() });
      setOrder(prev => ({ ...prev, senderNumber: senderNumber.trim(), paymentSenderSubmitted: true, customerMarkedPaid: true }));
      toast.success('Payment info saved! Admin will verify shortly.');
    } catch {
      toast.error('Failed to save');
    } finally {
      setSavingSender(false);
    }
  };

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', padding: '120px' }}><div className="spinner" /></div>;
  if (!order) return <div style={{ textAlign: 'center', padding: '100px', color: 'var(--text3)' }}>Order not found</div>;

  const enabledMethods = paymentSettings ? METHODS.filter(m => paymentSettings[m.key]) : [];
  const showPaymentBox = paymentSettings && order.orderStatus !== 'cancelled' && order.paymentStatus !== 'paid' && !order.adminVerifiedPaid;

  return (
    <div className="order-detail-page">
      <div className="container">
        <div className="od-header">
          <div>
            <h1>Order <span className="gradient-text">#{order._id.slice(-8).toUpperCase()}</span></h1>
            <p className="od-date">{new Date(order.createdAt).toLocaleString('en-BD')}</p>
          </div>
          <div className="od-status-wrap">
            <span className={`badge ${statusColor(order.orderStatus)}`} style={{ fontSize: '0.9rem', padding: '6px 18px' }}>
              {statusLabel(order.orderStatus)}
            </span>
          </div>
        </div>

        <div className="od-grid">
          {/* Items */}
          <div className="od-main">
            <div className="od-card card">
              <h3>📦 Order Items</h3>
              {order.items?.map((item, i) => (
                <div key={i} className="od-item">
                  <img src={item.thumbnail || 'https://via.placeholder.com/70'} alt={item.name} />
                  <div className="od-item-info">
                    <span className="od-item-name">{item.name}</span>
                    <span className="od-item-meta">Size: {item.size} × {item.quantity}</span>
                    {item.isPreOrder && (
                      <span className="od-preorder">
                        Pre-Order ({item.preOrderType === '50percent' ? '50% Advance' : '100% Advance'})
                      </span>
                    )}
                  </div>
                  <span className="od-item-price">{formatPrice(item.price * item.quantity)}</span>
                </div>
              ))}
            </div>

            {/* Shipping Address */}
            <div className="od-card card">
              <h3>🚚 Shipping Address</h3>
              <div className="address-block">
                <strong>{order.shippingAddress?.name}</strong>
                <p>{order.shippingAddress?.phone}</p>
                <p>{order.shippingAddress?.street}</p>
                <p>{order.shippingAddress?.city}, {order.shippingAddress?.district}</p>
                <p>{order.shippingAddress?.insideDhaka ? 'Inside Dhaka' : 'Outside Dhaka'}</p>
              </div>
            </div>
          </div>

          {/* Summary */}
          <div className="od-sidebar">
            <div className="od-card card">
              <h3>💰 Payment Summary</h3>
              <div className="od-totals">
                <div className="od-row"><span>Subtotal</span><span>{formatPrice(order.subtotal)}</span></div>
                <div className="od-row"><span>Shipping</span><span>{formatPrice(order.shippingCharge)}</span></div>
                {order.advancePaid > 0 && (
                  <div className="od-row"><span>Advance Paid</span><span>{formatPrice(order.advancePaid)}</span></div>
                )}
                <div className="od-row total-row">
                  <span>Total</span>
                  <span className="od-total-price">{formatPrice(order.totalPrice)}</span>
                </div>
              </div>

              <div className="payment-status-block">
                <div className="ps-item">
                  <span>Payment Status</span>
                  <span className={`badge ${statusColor(order.paymentStatus)}`}>{statusLabel(order.paymentStatus)}</span>
                </div>
                <div className="ps-item">
                  <span>Order Status</span>
                  <span className={`badge ${statusColor(order.orderStatus)}`}>{statusLabel(order.orderStatus)}</span>
                </div>
              </div>
            </div>

            {/* Payment Instructions */}
            {showPaymentBox && (
              <div className="od-card card od-payment-card">
                <h3>💸 Send Delivery Fee</h3>

                <div className="od-pay-amount">
                  <span className="od-pay-amount-label">Amount to Send</span>
                  <span className="od-pay-amount-val">{formatPrice(order.shippingCharge)}</span>
                  <span className="od-pay-amount-sub">Delivery charge only</span>
                </div>

                <div className="od-pay-number-row">
                  <span className="od-pay-to-label">Send to</span>
                  <div className="od-pay-number-box">
                    <span className="od-pay-number">{paymentSettings.number}</span>
                    <button
                      className="pay-copy-btn"
                      type="button"
                      onClick={() => { navigator.clipboard.writeText(paymentSettings.number); toast.success('Copied!'); }}
                    >
                      📋
                    </button>
                  </div>
                </div>

                {enabledMethods.length > 0 && (
                  <div className="od-pay-methods">
                    {enabledMethods.map(m => (
                      <span
                        key={m.key}
                        className="pay-method-chip"
                        style={{ color: m.color, borderColor: m.color, background: m.bg }}
                      >
                        {m.icon} {m.label}
                      </span>
                    ))}
                  </div>
                )}

                {paymentSettings.note && (
                  <p className="od-pay-note">ℹ️ {paymentSettings.note}</p>
                )}

                {/* Sender number input */}
                {!order.paymentSenderSubmitted ? (
                  <form onSubmit={saveSenderNumber} className="od-sender-form">
                    <label className="form-label" style={{ marginTop: 14 }}>
                      Enter the number you sent from *
                    </label>
                    <div className="od-sender-row">
                      <input
                        className="form-input"
                        placeholder="01XXXXXXXXX"
                        value={senderNumber}
                        onChange={e => setSenderNumber(e.target.value)}
                        required
                        style={{ flex: 1 }}
                      />
                      <button type="submit" className="btn btn-primary od-sender-btn" disabled={savingSender}>
                        {savingSender ? '...' : '✅ Submit'}
                      </button>
                    </div>
                  </form>
                ) : (
                  <div className="od-sender-submitted">
                    <span>✅ Sent from: <strong>{order.senderNumber}</strong></span>
                    <span className="od-sender-pending">Awaiting admin verification</span>
                  </div>
                )}
              </div>
            )}

            {/* Customer Actions */}
            {!order.customerMarkedPaid && order.orderStatus !== 'cancelled' && !showPaymentBox && (
              <div className="od-card card">
                <h3>✅ Mark as Paid</h3>
                <p className="mark-paid-desc">
                  Made full payment or advance payment? Click below to notify the admin.
                  Admin will verify and update your order status.
                </p>
                <button
                  className="btn btn-success"
                  style={{ width: '100%', justifyContent: 'center' }}
                  onClick={markAsPaid}
                  disabled={marking}
                >
                  {marking ? 'Updating...' : '✅ I Have Paid'}
                </button>
              </div>
            )}

            {order.customerMarkedPaid && !order.adminVerifiedPaid && (
              <div className="od-card card pending-verify">
                <p>⏳ Payment submitted! Awaiting admin verification.</p>
              </div>
            )}

            {order.adminVerifiedPaid && (
              <div className="od-card card verified-paid">
                <p>✅ Payment verified by admin!</p>
              </div>
            )}

            {order.shippingReceived && !order.adminVerifiedPaid && (
              <div className="od-card card verified-paid">
                <p>✅ Shipping charge received & verified by admin!</p>
              </div>
            )}

            {order.note && (
              <div className="od-card card">
                <h3>📝 Order Note</h3>
                <p style={{ color: 'var(--text3)', fontSize: '0.9rem' }}>{order.note}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderDetailPage;
